/**
 * Material Group Definitions and Attributes
 * Shared between Server and Client for consistent schemas.
 */

export type MaterialGroupCode = 'go' | 'van' | 'nk' | 'bb' | 'td'

export interface MaterialGroupAttributeDef {
  key: string
  label: string
  width: number
  numeric?: boolean
}

export interface MaterialGroupDef {
  code: MaterialGroupCode
  name: string
  attributes: MaterialGroupAttributeDef[]
}

export const MATERIAL_GROUPS: MaterialGroupDef[] = [
  {
    code: 'go',
    name: 'Phôi gỗ',
    attributes: [
      { key: 'wood', label: 'Loại gỗ', width: 90 },
      { key: 'd', label: 'Dày (mm)', width: 70, numeric: true },
      { key: 'r', label: 'Rộng (mm)', width: 76, numeric: true },
      { key: 'l', label: 'Dài (mm)', width: 72, numeric: true },
    ],
  },
  {
    code: 'van',
    name: 'Ván công nghiệp',
    attributes: [
      { key: 'face', label: 'Bề mặt / phẩm cấp', width: 130 },
      { key: 'd', label: 'Dày (mm)', width: 70, numeric: true },
      { key: 'r', label: 'Rộng (mm)', width: 76, numeric: true },
      { key: 'l', label: 'Dài (mm)', width: 72, numeric: true },
    ],
  },
  {
    code: 'nk',
    name: 'Ngũ kim & phụ kiện',
    attributes: [
      { key: 'quy_cach', label: 'Quy cách', width: 140 },
      { key: 'mau', label: 'Màu', width: 100 },
    ],
  },
  {
    code: 'bb',
    name: 'Bao bì',
    attributes: [
      { key: 'type', label: 'Loại', width: 140 },
      { key: 'size', label: 'Kích thước', width: 120 },
      { key: 'flute', label: 'Sóng / định lượng', width: 120 },
    ],
  },
  {
    code: 'td',
    name: 'Vật tư thông dụng',
    attributes: [
      { key: 'spec', label: 'Quy cách / thông số', width: 190 },
    ],
  },
]

export function getMaterialGroup(code: string | null | undefined): MaterialGroupDef | undefined {
  if (!code) return undefined
  return MATERIAL_GROUPS.find((g) => g.code === code)
}

export function getGroupDef(code: string | null | undefined): MaterialGroupDef {
  return (
    getMaterialGroup(code) || {
      code: 'td',
      name: 'Chưa phân nhóm',
      attributes: [],
    }
  )
}

const ATTR_ALIASES: Record<string, string[]> = {
  wood: ['wood', 'loai_go'],
  face: ['face', 'be_mat'],
  d: ['d', 'day', 'thickness'],
  r: ['r', 'rong', 'width'],
  l: ['l', 'dai', 'length'],
  spec: ['spec', 'quy_cach'],
  quy_cach: ['quy_cach', 'spec'],
  mau: ['mau', 'color'],
  brand: ['brand', 'hang', 'origin'],
  box_type: ['box_type', 'loai_thung'],
  dim: ['dim', 'kich_thuoc'],
  flute: ['flute', 'song'],
}

export function formatMaterialAttributes(
  groupCode: string | null | undefined,
  attrs: Record<string, any> | null | undefined,
): string {
  if (!attrs || typeof attrs !== 'object') return ''
  const grp = getMaterialGroup(groupCode)
  if (!grp) {
    return Object.values(attrs)
      .filter((v) => v !== '' && v != null)
      .join(' · ')
  }
  return grp.attributes
    .map((a) => {
      if (attrs[a.key] !== undefined && attrs[a.key] !== null) return attrs[a.key]
      const aliases = ATTR_ALIASES[a.key] || []
      for (const al of aliases) {
        if (attrs[al] !== undefined && attrs[al] !== null) return attrs[al]
      }
      return ''
    })
    .filter((v) => v !== '' && v != null)
    .join(' · ')
}

export function detectMaterialGroup(name: string, kind?: string | null): MaterialGroupCode {
  const n = (name || '').toLowerCase()
  const isRaw = kind === 'RAW'
  if (isRaw && /(phôi|\blam\b|lam gỗ|chân|khung|nan|thanh|tay vịn)/i.test(n)) {
    return 'go'
  }
  if (/(veneer|laminate|mdf|hdf|plywood|ván|melamine|tấm đáy|tấm lưng)/i.test(n)) {
    return 'van'
  }
  if (/(thùng|carton|xốp|mút|foam|góc giấy|dây đai|túi|tem|băng keo|hướng dẫn lắp)/i.test(n)) {
    return 'bb'
  }
  if (
    /(bản lề|ray|tay nắm|ốc|vít|bu[- ]?lông|bulong|pát|chốt gỗ|khóa|bánh xe|núm kéo|nam châm|lò xo)/i.test(n) ||
    (kind === 'AUX' && /chân/i.test(n))
  ) {
    return 'nk'
  }
  return 'td'
}
